/**
 * Member-app user-scoped analytics events (`EventTrackingService.track` /
 * Mixpanel). Distinct from backend analytics (`BackendUserEvent`) and
 * persisted `user_events` (`PersistedUserEvent`).
 */
export enum AnalyticsUserEvent {
    VIEW = 'View',
    BACK = 'BackButtonPressed',
    REFRESH_STALE_PAGE = 'RefreshStalePage',
    OPEN_MESSAGING = 'OpenGuideMessaging',

    // Money Map events
    OPEN_PREVIEW_MONEY_MAP = 'OpenPreviewMoneyMapPressed',
    OPEN_COMPLETED_MONEY_MAP = 'OpenCompletedMoneyMapPressed',
    OPEN_MONEY_MAP_WORKFLOW = 'OpenMoneyMapWorkflowPressed',
    OPEN_MONEY_MAP_GUIDE_PROMPT = 'OpenMoneyMapGuidePromptPressed',
    MONEY_MAP_CHART_INTERACTION = 'MoneyMapChartInteraction',
    MONEY_MAP_WORKFLOW_STEP_TILE = 'MoneyMapWorkflowStepTilePressed',
    MONEY_MAP_WORKFLOW_START_CTA = 'MoneyMapWorkflowStartCtaPressed',
    MONEY_MAP_WORKFLOW_STEP_SUBMIT_REVIEW = 'MoneyMapWorkflowStepSubmit_Review',
    MONEY_MAP_WORKFLOW_STEP_SUBMIT_CASH_ACCOUNTS = 'MoneyMapWorkflowStepSubmit_CashAccounts',
    MONEY_MAP_WORKFLOW_STEP_SUBMIT_CARD_ACCOUNTS = 'MoneyMapWorkflowStepSubmit_CardAccounts',
    MONEY_MAP_WORKFLOW_STEP_SUBMIT_APEX_ACCOUNTS = 'MoneyMapWorkflowStepSubmit_ApexAccounts',
    MONEY_MAP_WORKFLOW_STEP_SUBMIT_INCOME_SPLIT = 'MoneyMapWorkflowStepSubmit_IncomeSplit',
    MONEY_MAP_WORKFLOW_ACCOUNTS_COMPLETE = 'MoneyMapWorkflowAccountsComplete',
    MONEY_MAP_WORKFLOW_AUTOPAY_PROMPT = 'MoneyMapWorkflowAutoPayPrompt',
    MONEY_MAP_WORKFLOW_INCOME_SPLIT_COMPLETE = 'MoneyMapWorkflowIncomeSplitComplete',
    MONEY_MAP_WORKFLOW_CASH_ACCOUNTS_SHEET_OPENED = 'MoneyMapWorkflowCashAccountsSheetOpened',
    MONEY_MAP_WORKFLOW_CASH_ACCOUNTS_SHEET_CLOSED = 'MoneyMapWorkflowCashAccountsSheetClosed',
    MONEY_MAP_WORKFLOW_CARD_ACCOUNTS_SHEET_OPENED = 'MoneyMapWorkflowCardAccountsSheetOpened',
    MONEY_MAP_WORKFLOW_CARD_ACCOUNTS_SHEET_CLOSED = 'MoneyMapWorkflowCardAccountsSheetClosed',
    INCOME_SPLIT_INFO_SHEET_DISMISSED = 'IncomeSplitInfoSheetDismissed',
    INCOME_SPLIT_SUCCESS_INTERACTION = 'IncomeSplitSuccessInteraction',
    INCOME_SPLIT_ENABLED = 'IncomeSplit_Enabled',
    INCOME_SPLIT_DISABLED = 'IncomeSplit_Disabled',
    INCOME_SPLIT_INCOME_TYPE_CHANGED = 'IncomeSplit_IncomeTypeChanged',
    INCOME_SPLIT_ACCOUNTS_UPDATED = 'IncomeSplit_AccountsUpdated',
    INCOME_SPLIT_OVERVIEW_VIEW = 'IncomeSplitOverview',
    INCOME_SPLIT_DEPOSIT_DETAILS_VIEW = 'IncomeSplitDepositDetails',
    INCOME_SPLIT_RULES_VIEW = 'IncomeSplitRules',
    INCOME_SPLIT_RULES_CLICKED = 'IncomeSplitRulesClicked',
    INCOME_SPLIT_DEPOSIT_DETAILS_CLICKED = 'IncomeSplitDepositDetailsClicked',
    INCOME_SPLIT_RULE_SET_EXPANDED = 'IncomeSplitRuleSetExpanded',

    // Banking Adoption events
    BANKING_ADOPTION_ITEM_TOGGLED = 'BankingAdoptionChecklistItemToggled',
    BANKING_ADOPTION_ITEM_CTA_PRESSED = 'BankingAdoptionChecklistItemCtaPressed',
    BANKING_ADOPTION_BILL_ITEM_TOGGLED = 'BankingAdoptionBillItemToggled',
    BANKING_ADOPTION_BILL_ROW_PRESSED = 'BankingAdoptionBillRowPressed',
    BANKING_ADOPTION_ADD_NEW_BILL_PRESSED = 'BankingAdoptionAddNewBillPressed',

    // Direct Deposit events
    DIRECT_DEPOSIT_SWITCH_OPENED = 'DirectDepositSwitchOpened',
    DIRECT_DEPOSIT_SWITCH_EXITED = 'DirectDepositSwitchExited',
    DIRECT_DEPOSIT_SWITCH_SUCCESS = 'DirectDepositSwitchSuccess',
    DIRECT_DEPOSIT_SWITCH_ERROR = 'DirectDepositSwitchError',
    DIRECT_DEPOSIT_SWITCH_VIEW_MANUAL = 'DirectDepositSwitchViewManualInstructions',
    DIRECT_DEPOSIT_SWITCH_MAYBE_LATER = 'DirectDepositSwitchMaybeLater',
    DIRECT_DEPOSIT_SWITCH_EXPLAINER_EXIT = 'DirectDepositSwitchExplainerExit',
    DIRECT_DEPOSIT_SWITCH_ACCOUNT_SELECTED = 'DirectDepositSwitchAccountSelected',
    DIRECT_DEPOSIT_SWITCH_SELECT_ACCOUNT_EXIT = 'DirectDepositSwitchSelectAccountExit',
    DIRECT_DEPOSIT_SWITCH_MANUAL_SWITCH_EXIT = 'DirectDepositSwitchManualSwitchExit', // back button
    DIRECT_DEPOSIT_SWITCH_MANUAL_SWITCH_DONE = 'DirectDepositSwitchManualSwitchDone', // done button

    // Beneficiaries events
    BENEFICIARY_ADDED = 'BeneficiaryAdded',

    // Account verification letter events
    ACCOUNT_VERIFICATION_LETTER_DOWNLOAD_ERROR = 'AccountVerificationLetterDownloadError',

    // Signup funnel events
    CMM_SESSION_NEW = 'CMM_SessionNew',
    CMM_SESSION_RESTORED = 'CMM_SessionRestored',
    SIGNUP_FUNNEL_INIT = 'SignupFunnel_Init',
    SIGNUP_FUNNEL_CMM_VIEW_FIRST_QUESTION = 'SignupFunnel_CMM_ViewFirstQuestion',
    SIGNUP_FUNNEL_CMM_ANSWER_FIRST_QUESTION = 'SignupFunnel_CMM_AnswerFirstQuestion',
    SIGNUP_FUNNEL_CMM_BRANCH_SELECTED = 'SignupFunnel_CMM_BranchSet',
    SIGNUP_FUNNEL_CMM_CURRENT_STEP_UPDATED = 'SignupFunnel_CMM_CurrentStepUpdated',
    SIGNUP_FUNNEL_CMM_GO_TO_RESULTS = 'SignupFunnel_CMM_GoToResults',
    SIGNUP_FUNNEL_CMM_RESULTS_SLIDE = 'SignupFunnel_CMM_ResultsSlide',
    CMM_RESET_THREAD = 'CMM_ResetThread',
    CMM_INPUT_MULTI_SELECT_SUBMITTED = 'CMM_InputMultiSelectSubmitted',
    /**
     * A pure single-select (one option, no free-text — e.g. the yes/no partner
     * question) that auto-submits on tap. Distinct from the multi-select event
     * so analytics isn't mislabeled.
     */
    CMM_INPUT_SINGLE_SELECT_SUBMITTED = 'CMM_InputSingleSelectSubmitted',
    CMM_INPUT_CONFIRMATION_CONFIRMED = 'CMM_InputConfirmationConfirmed',
    CMM_INPUT_CONFIRMATION_CHANGE_REQUESTED = 'CMM_InputConfirmationChangeRequested',
    CMM_SPEND_GOALS_WIDGET_SLIDER = 'CMM_SpendGoalsWidgetSlider',
    CMM_SPEND_GOALS_CONFIRMED = 'CMM_SpendGoalsConfirmed',
    CMM_RESULTS_REVEALED = 'CMM_ResultsRevealed',
    CMM_RESULTS_MM = 'CMM_Results_MM',
    CMM_RESULTS_EXPLAINER = 'CMM_Results_Explainer',
    CMM_RESULTS_SYSTEM = 'CMM_Results_System',
    CMM_RESULTS_GRAPH = 'CMM_Results_Graph',
    CMM_RESULTS_SUMMARY = 'CMM_Results_Summary',
    CMM_RESULTS_PREV = 'CMM_ResultsPrev',
    CMM_RESULTS_NEXT = 'CMM_ResultsNext',
    CMM_RESULTS_SLIDE_NEXT_BUTTON = 'CMM_ResultsSlideNextButtonPress',
    CMM_RESULTS_TOOLTIP_PRESS = 'CMM_ResultsTooltipPress',
    CMM_RESULTS_TOOLTIPS_DONE = 'CMM_ResultsTooltipsDone',
    CMM_RESULTS_SPEND_GOALS_SLIDER = 'CMM_ResultsSpendGoalsSlider',
    CMM_RESULTS_GOALS_CARDS_EXPANDED = 'CMM_ResultsGoalsCardsExpanded',
    CMM_RESULTS_INVEST_CONTRIB_SLIDER = 'CMM_ResultsInvestContributeSlider',
    CMM_RESULTS_INVEST_TIME_SLIDER = 'CMM_ResultsInvestTimeSlider',
    CMM_RESULTS_THREAD_SAVE_ERROR = 'CMM_ResultsThreadSaveError',
    CMM_RESULTS_ARTIFACT_FINAL_PAGE_SEGMENT = 'CMM_ResultsArtifactFinalPageSegment',
    /** Bills confirmed / building plan UI shown, but show-intake-summary never arrived. */
    CMM_BUILDING_PLAN_STUCK_RECOVERY = 'CMM_BuildingPlanStuckRecovery',
    CMM_HOME_GOAL_TILE_PRESSED = 'CMM_HomeGoalTilePressed',
    SIGNUP_FUNNEL_CMM_RESULTS_SUBMIT = 'SignupFunnel_CMM_ResultsSubmit',
    SIGNUP_FUNNEL_SUBSCRIPTION_STARTED = 'SignupFunnel_SubscriptionStarted',
    SIGNUP_FUNNEL_ACCOUNT_SUBMIT = 'SignupFunnel_AccountContactInfoSubmit',
    SIGNUP_FUNNEL_ACCOUNT_CREATED = 'Signed Up',
    SIGNUP_FUNNEL_GET_APP_PROMPT = 'SignupFunnel_GetAppPrompt',
    SIGNUP_FUNNEL_DOWNLOAD_APP_PRESSED = 'SignupFunnel_DownloadAppPressed',
    SIGNUP_FUNNEL_MEMBERSHIP_SUBMIT = 'SignupFunnel_MembershipSubmit',
    SIGNUP_FUNNEL_SURVEY_EMAIL_SUBMIT = 'SignupFunnel_Survey_EmailSubmit',
    MEMBERSHIP_PURCHASE_PUBLISHED = 'membershipPurchasePublished',
    SIGNUP_FUNNEL_NEW_MEMBER_ACCOUNT_CREATED = 'SignupFunnel_AccountCreated',
    SIGNUP_FUNNEL_NEW_MEMBER_INFO_SUBMIT = 'SignupFunnel_NewMemberInfo',
    SIGNUP_FUNNEL_RETURNING_MEMBER_INFO_SUBMIT = 'SignupFunnel_ReturningMemberInfo',

    // Onboarding events
    ONBOARDING_FUNNEL_ACCOUNTS_CONNECTED = 'OnboardingFunnel_AccountsConnected',
    ONBOARDING_FUNNEL_BILLS_CONFIRMED = 'OnboardingFunnel_BillsConfirmed',
    ONBOARDING_FUNNEL_SPEND_COMPLETED = 'OnboardingFunnel_SpendCompleted',
    ONBOARDING_FUNNEL_SPINWHEEL_COMPLETED = 'OnboardingFunnel_SpinwheelCompleted',
    ONBOARDING_FUNNEL_EXPENSES_COMPLETED = 'OnboardingFunnel_ExpensesCompleted',
    ONBOARDING_HOME_EXPLORE_APP = 'OnboardingHome_ExploreApp',
    ONBOARDING_BOOK_1ST_SESSION_REACHED_LAST_DAY = 'Onboarding_BookAvailableGuidesReachedLastDay',
    ONBOARDING_BOOK_1ST_SESSION_DISMISS_CONFIRM = 'Onboarding_Book1stSession_DismissConfirm',
    ONBOARDING_BOOK_1ST_SESSION_CONFIRM_SHEET = 'Onboarding_Book1stSession_ConfirmSheetOpened',
    ONBOARDING_BOOK_1ST_SESSION_CONFIRM_PRESSED = 'Onboarding_Book1stSession_ConfirmPressed',
    ONBOARDING_BOOK_1ST_SESSION_DISMISS_BOOK_FURTHER_OUT = 'Onboarding_Book1stSession_DismissBookFurtherOut',
    ONBOARDING_BOOK_1ST_SESSION_SCROLLED_ALL_GUIDES = 'Onboarding_Book1stSession_ScrolledAllGuides',
    ONBOARDING_BOOK_1ST_SESSION_DAY_NAV_PREV = 'Onboarding_Book1stSession_DayNavPrev',
    ONBOARDING_BOOK_1ST_SESSION_DAY_NAV_NEXT = 'Onboarding_Book1stSession_DayNavNext',
    ONBOARDING_BOOK_1ST_SESSION_VIEW_GUIDE_DETAILS = 'Onboarding_Book1stSession_ViewGuideDetails',
    ONBOARDING_BOOK_1ST_SESSION_AVAILABILITY_DATA = 'Onboarding_Book1stSession_AvailabilityData',
    MAGIC_SAVE_DIDNT_BOOK_SCHEDULE = 'Magic_SaveDidntBookSchedule',
    ONBOARDING_PATH_SELECTED = 'OnboardingPathSelected',
    ONBOARDING_GOYO_OPEN_SHEET = 'OnboardingGoyoOpenSheet',
    ONBOARDING_GOYO_EDITED = 'OnboardingGoyoEdited',
    ONBOARDING_GOYO_SAVED = 'OnboardingGoyoSaved',
    ONBOARDING_GOYO_LOOKS_GOOD = 'OnboardingGoyoLooksGood',
    ONBOARDING_GOYO_CONFIRMED = 'OnboardingGoyoConfirmed',
    ONBOARDING_GOYO_PRESS_ACTIVATE_MONEY_SYSTEM = 'OnboardingGoyoPressActivateMoneySystem',
    ONBOARDING_INFO_SHEET_CLOSED = 'OnboardingInfoSheetClosed',

    // IM events
    TRUSTED_CONTACT_SAVE = 'TrustedContact_Save',
    TRUSTED_CONTACT_DELETE = 'TrustedContact_Delete',
    IM_OPEN_ANOTHER_ACCOUNT = 'ImOpenAnotherAccount',
    IM_DEPOSIT_INITIATED = 'ImDepositInitiated',
    IM_DEPOSIT_FAILED = 'ImDepositFailed',
    IM_WITHDRAWAL_INITIATED = 'ImWithdrawalInitiated',

    // Referrals events
    REFERRALS_CTA_PRESSED = 'RAF_ReferralsCTAPressed',
    REFERRALS_INVITE_FRIENDS_PRESSED = 'RAF_InviteFriendsCTAPressed',
    REFERRALS_COPY_LINK_PRESSED = 'RAF_CopyLinkCTAPressed',

    // Other
    FDX_OAUTH_COMPLETE = 'FDXOAuthComplete',
    FF_DEBUGGING = 'FruitfulDebugging',

    // Free to Spend
    FTS_OPEN_SETTINGS = 'SpendHero_OpenSettings',
    FTS_CHANGE_PERIOD = 'SpendWidget_ChangePeriod',
    FTS_TAP_ACCOUNT = 'SpendHero_TapAccount',
    FTS_SWIPE_CARD = 'SpendHero_Swipe',
    FTS_TAP_GRAPH = 'SpendWidget_GraphInteraction',
    FTS_CHANGE_ACCOUNT = 'SpendWidget_ChangeAccount',
    FTS_TAP_STATUS = 'SpendWidget_TapStatus',
    FTS_TAP_TRANSACTION = 'SpendWidget_TapTransaction',
    FTS_TAP_BAR = 'SpendWidget_TapBar',

    // AI Chat events
    AI_CHAT_AGENT_RESPONSE_TEXT_RENDERED = 'AIChat_AgentResponseTextRendered',
    AI_CHAT_AGENT_RESPONSE_WIDGET_RENDERED = 'AIChat_AgentResponseWidgetRendered',
    AI_CHAT_USER_RESPONSE_RENDERED = 'AIChat_UserResponseRendered',
    AI_CHAT_INPUT_TEXT_RESPONSE_SENT = 'AIChat_InputTextResponseSent',
    AI_CHAT_INPUT_STOP_BUTTON_PRESSED = 'AIChat_InputStopButtonPressed',
    AI_CHAT_OFF_TOPIC_DETECTED = 'AIChat_OffTopicDetected',
    AI_CHAT_OFF_TOPIC_CUTOFF_REACHED = 'AIChat_OffTopicCutoffReached',
    AI_CHAT_SUPPORT_ESCALATION_DETECTED = 'AIChat_SupportEscalationDetected',
    AI_CHAT_SCROLLED_UP = 'AIChat_ScrolledUp',
    AI_CHAT_WIDGET_CHART_RENDERED = 'AIChat_Widget_Chart_Rendered',
    AI_CHAT_WIDGET_CHART_SELECT = 'AIChat_Widget_Chart_ChartItemSelect',
    AI_CHAT_WIDGET_CHART_LEGEND_SELECT = 'AIChat_Widget_Chart_LegendItemSelect',
    AI_CHAT_WIDGET_FREE_TO_SPEND_RENDERED = 'AIChat_Widget_FreeToSpend_Rendered',
    AI_CHAT_WIDGET_FREE_TO_SPEND_OPEN_STATUS = 'AIChat_Widget_FreeToSpend_OpenStatus',
    AI_CHAT_WIDGET_FREE_TO_SPEND_OPEN_TRANSACTIONS = 'AIChat_Widget_FreeToSpend_OpenTransactions',
    AI_CHAT_WIDGET_TRANSACTIONS_RENDERED = 'AIChat_Widget_Transactions_Rendered',
    AI_CHAT_WIDGET_TRANSACTIONS_OPEN = 'AIChat_Widget_Transactions_Open',
    AI_CHAT_WIDGET_MONEY_MAP_FILTER = 'AIChat_Widget_MoneyMap_Filter',
    AI_CHAT_WIDGET_ACCOUNT_BALANCES_RENDERED = 'AIChat_Widget_AccountBalances_Rendered',
    AI_CHAT_WIDGET_ACCOUNT_BALANCES_OPEN = 'AIChat_Widget_AccountBalances_Open',
    AI_CHAT_WIDGET_INCOME_SPLIT_RENDERED = 'AIChat_Widget_IncomeSplit_Rendered',
    AI_CHAT_WIDGET_CARD_DETAILS_RENDERED = 'AIChat_Widget_CardDetails_Rendered',
    AI_CHAT_WIDGET_INLINE_DATA_TABLE_RENDERED = 'AIChat_Widget_InlineDataTable_Rendered',
    AI_CHAT_WIDGET_SPEND_GOALS_RENDERED = 'AIChat_Widget_SpendGoals_Rendered',
    AI_CHAT_WIDGET_MULTI_SELECT_RENDERED = 'AIChat_Widget_MultiSelect_Rendered',
    AI_CHAT_WIDGET_SEND_TO_GUIDE_RENDERED = 'AIChat_Widget_SendToGuide_Rendered',
    AI_CHAT_WIDGET_EMAIL_SUPPORT_RENDERED = 'AIChat_Widget_EmailSupport_Rendered',
    AI_CHAT_SUGGESTED_RESPONSE_SELECTED = 'AIChat_SuggestedResponseSelected',
    FRUITFUL_AI_CHAT_NEW_SESSION = 'FruitfulAIChat_NewSession',
}
